import { Controller } from "@hotwired/stimulus"

const KEY = "jane.community.fullscreen"

// Full screen for Community's worlds (the Square, San Francisco): the world takes the whole window,
// Jane's header and app rail become an overlay behind the world bar's menu button, and the chat
// floats over the world as a glass panel. On by default - you came to be in the world - and
// remembered per browser once you switch it off ("Side by side").
//
// It works by classes on <html>, so the page underneath is the same page: cc-immersive (full
// screen), cc-nav-open (the overlay is showing). Turbo navigating away drops both. The browser's
// own full screen (no tabs, no address bar) is one more button, for a big screen or a demo.
export default class extends Controller {
    static targets = ["navButton", "layoutButton", "fullscreenButton"]

    connect() {
        this.onKey = (e) => { if (e.key === "Escape" && this.navOpen) { e.stopImmediatePropagation(); this.closeNav() } }
        this.onClick = (e) => {
            if (!this.navOpen) return
            if (e.target.closest(".assistant-header, .assistant-bottom-nav, .cc-rooms, .cc-world-bar, .offcanvas, .universal-search-backdrop")) return
            this.closeNav()
        }
        this.onFullscreen = () => this.#sync()
        window.addEventListener("keydown", this.onKey, true)
        document.addEventListener("pointerdown", this.onClick, true)
        document.addEventListener("fullscreenchange", this.onFullscreen)
        this.#set(this.#prefers())
    }

    disconnect() {
        window.removeEventListener("keydown", this.onKey, true)
        document.removeEventListener("pointerdown", this.onClick, true)
        document.removeEventListener("fullscreenchange", this.onFullscreen)
        document.documentElement.classList.remove("cc-immersive", "cc-nav-open")
    }

    toggleNav() {
        this.navOpen ? this.closeNav() : this.#openNav()
    }

    closeNav() {
        this.navOpen = false
        document.documentElement.classList.remove("cc-nav-open")
        this.#sync()
    }

    // The keyboard button: each world shows its own controls (its cheat sheet, or the Square's tip).
    help() {
        window.dispatchEvent(new CustomEvent("jane-worlds:help"))
    }

    toggleImmersive() {
        const on = !document.documentElement.classList.contains("cc-immersive")
        try { window.localStorage.setItem(KEY, on ? "on" : "off") } catch (_e) { /* private mode */ }
        this.#set(on)
    }

    async toggleFullscreen() {
        try {
            if (document.fullscreenElement) await document.exitFullscreen()
            else await document.documentElement.requestFullscreen({ navigationUI: "hide" })
        } catch (_e) { /* not allowed here (an iframe, an old Safari) */ }
        if (document.fullscreenElement) this.#set(true)
    }

    #openNav() {
        this.navOpen = true
        document.documentElement.classList.add("cc-nav-open")
        this.#sync()
    }

    #set(on) {
        document.documentElement.classList.toggle("cc-immersive", on)
        if (!on) this.closeNav()
        this.#sync()
        // the worlds size themselves to their box; tell anything that measures once
        window.dispatchEvent(new CustomEvent("jane:panels-changed"))
        window.dispatchEvent(new Event("resize"))
    }

    #prefers() {
        try { return window.localStorage.getItem(KEY) !== "off" } catch (_e) { return true }
    }

    #sync() {
        const on = document.documentElement.classList.contains("cc-immersive")
        if (this.hasNavButtonTarget) {
            this.navButtonTarget.hidden = !on
            this.navButtonTarget.setAttribute("aria-expanded", this.navOpen ? "true" : "false")
        }
        if (this.hasLayoutButtonTarget) {
            this.layoutButtonTarget.setAttribute("aria-pressed", on ? "true" : "false")
            this.layoutButtonTarget.title = on ? "Side by side: show Jane around the world" : "Full screen: the world fills the window"
            this.layoutButtonTarget.querySelector("i").className = `bi ${on ? "bi-fullscreen-exit" : "bi-arrows-fullscreen"}`
        }
        if (this.hasFullscreenButtonTarget) {
            this.fullscreenButtonTarget.hidden = !document.fullscreenEnabled
            this.fullscreenButtonTarget.setAttribute("aria-pressed", document.fullscreenElement ? "true" : "false")
        }
    }
}
